import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ArrowRight, ArrowLeft } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";

export const AuthModal = () => {
    const { authOpen, setAuthOpen, authMode, setAuthMode, signup, login, onAuthSuccess, errMsg } =
        useAuth();
    const { lang } = useLang();

    const [step, setStep] = useState(1);
    const [form, setForm] = useState({ name: "", phone: "", email: "", password: "" });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const isSignup = authMode === "signup";

    const reset = () => {
        setStep(1);
        setForm({ name: "", phone: "", email: "", password: "" });
        setError("");
    };

    const close = () => {
        setAuthOpen(false);
        reset();
    };

    const handleStep1Next = (e) => {
        e.preventDefault();
        setError("");
        if (isSignup) {
            if (!form.name.trim() || form.name.trim().length < 2) {
                setError(lang === "en" ? "Please enter your name" : "మీ పేరు నమోదు చేయండి");
                return;
            }
        }
        const phoneDigits = form.phone.replace(/\D/g, "").replace(/^91/, "");
        if (phoneDigits.length !== 10 || !/^[6-9]/.test(phoneDigits)) {
            setError(
                lang === "en"
                    ? "Enter a valid 10-digit Indian mobile number"
                    : "10 అంకెల భారత మొబైల్ నంబర్ నమోదు చేయండి",
            );
            return;
        }
        setForm((f) => ({ ...f, phone: phoneDigits }));
        setStep(2);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        if (!form.password || form.password.length < 6) {
            setError(
                lang === "en" ? "Password must be at least 6 characters" : "పాస్‌వర్డ్ కనీసం 6 అక్షరాలు",
            );
            return;
        }
        setLoading(true);
        try {
            if (isSignup) {
                await signup({
                    name: form.name.trim(),
                    phone: form.phone,
                    email: form.email.trim() || null,
                    password: form.password,
                });
            } else {
                await login({ phone: form.phone, password: form.password });
            }
            reset();
            onAuthSuccess();
        } catch (err) {
            setError(errMsg(err));
        } finally {
            setLoading(false);
        }
    };

    const switchMode = () => {
        setAuthMode(isSignup ? "login" : "signup");
        reset();
    };

    if (!authOpen) return null;

    const t = (en, te) => (lang === "en" ? en : te);

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[color:var(--kp-ink)]/60 backdrop-blur-md"
                onClick={close}
                data-testid="auth-modal"
            >
                <motion.div
                    initial={{ opacity: 0, y: 30, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 30, scale: 0.98 }}
                    transition={{ duration: 0.5, ease: [0.19, 1, 0.22, 1] }}
                    onClick={(e) => e.stopPropagation()}
                    className="relative w-full max-w-[440px] bg-[color:var(--kp-cream)] border border-[color:var(--kp-ink)]/15 p-8 md:p-10"
                >
                    <button
                        onClick={close}
                        className="absolute top-4 right-4 p-1 hover:text-[color:var(--kp-red)]"
                        aria-label="Close"
                        data-testid="auth-close"
                    >
                        <X size={18} />
                    </button>

                    <div className="text-[10px] tracking-mono-wide text-[color:var(--kp-ink)]/60 mb-2">
                        {isSignup
                            ? t("STEP " + step + " / 2 · SIGN UP", "దశ " + step + " / 2 · నమోదు")
                            : t("LOG IN", "లాగిన్")}
                    </div>
                    <h2
                        className={`text-4xl md:text-5xl leading-[0.95] tracking-editorial mb-6 ${
                            lang === "en" ? "font-serif-display" : "font-telugu-display"
                        }`}
                    >
                        {isSignup ? (
                            step === 1 ? (
                                <>
                                    {t("Meet the", "మీరు")}{" "}
                                    <em className="italic text-[color:var(--kp-red)]">
                                        {t("kitchen.", "వంటగదిని కలవండి.")}
                                    </em>
                                </>
                            ) : (
                                <>
                                    {t("One last", "ఒక్క")}{" "}
                                    <em className="italic text-[color:var(--kp-red)]">
                                        {t("secret.", "రహస్యం.")}
                                    </em>
                                </>
                            )
                        ) : (
                            <>
                                {t("Welcome", "మళ్ళీ")}{" "}
                                <em className="italic text-[color:var(--kp-red)]">
                                    {t("back.", "స్వాగతం.")}
                                </em>
                            </>
                        )}
                    </h2>

                    {error && (
                        <div
                            className="mb-4 text-sm text-[color:var(--kp-red)] bg-[color:var(--kp-red)]/8 border border-[color:var(--kp-red)]/25 px-3 py-2"
                            data-testid="auth-error"
                        >
                            {error}
                        </div>
                    )}

                    {isSignup && step === 1 && (
                        <form onSubmit={handleStep1Next} className="space-y-4" data-testid="auth-form-signup-step1">
                            <Field
                                label={t("Your name", "మీ పేరు")}
                                value={form.name}
                                onChange={(v) => setForm((f) => ({ ...f, name: v }))}
                                testId="input-name"
                                autoFocus
                            />
                            <Field
                                label={t("Mobile number", "మొబైల్ నంబర్")}
                                prefix="+91"
                                inputMode="numeric"
                                value={form.phone}
                                onChange={(v) => setForm((f) => ({ ...f, phone: v.replace(/\D/g, "").slice(0, 10) }))}
                                testId="input-phone"
                            />
                            <Field
                                label={t("Email (optional)", "ఇమెయిల్ (ఐచ్ఛికం)")}
                                type="email"
                                value={form.email}
                                onChange={(v) => setForm((f) => ({ ...f, email: v }))}
                                testId="input-email"
                            />
                            <button
                                type="submit"
                                className="w-full inline-flex items-center justify-between bg-[color:var(--kp-red)] text-[color:var(--kp-cream)] px-5 py-4 rounded-full text-[12px] tracking-mono-wide hover:bg-[color:var(--kp-red-deep)] transition-colors"
                                data-testid="auth-continue"
                            >
                                <span>{t("Continue", "కొనసాగించు")}</span>
                                <ArrowRight size={14} />
                            </button>
                        </form>
                    )}

                    {isSignup && step === 2 && (
                        <form onSubmit={handleSubmit} className="space-y-4" data-testid="auth-form-signup-step2">
                            <div className="text-sm text-[color:var(--kp-ink)]/70 -mt-2">
                                {t("Signing up as", "నమోదు")} <span className="font-serif-display text-lg text-[color:var(--kp-ink)]">
                                    {form.name} · +91 {form.phone}
                                </span>
                            </div>
                            <Field
                                label={t("Set a password", "పాస్‌వర్డ్ ఏర్పాటు")}
                                type="password"
                                value={form.password}
                                onChange={(v) => setForm((f) => ({ ...f, password: v }))}
                                testId="input-password"
                                autoFocus
                            />
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={() => setStep(1)}
                                    className="inline-flex items-center justify-center border border-[color:var(--kp-ink)]/25 rounded-full px-4 py-4 text-[12px] tracking-mono-wide"
                                    data-testid="auth-back"
                                >
                                    <ArrowLeft size={14} />
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="flex-1 inline-flex items-center justify-between bg-[color:var(--kp-red)] text-[color:var(--kp-cream)] px-5 py-4 rounded-full text-[12px] tracking-mono-wide hover:bg-[color:var(--kp-red-deep)] disabled:opacity-60 transition-colors"
                                    data-testid="auth-submit"
                                >
                                    <span>
                                        {loading
                                            ? t("Creating…", "సృష్టిస్తోంది…")
                                            : t("Create account", "ఖాతా సృష్టించు")}
                                    </span>
                                    <ArrowRight size={14} />
                                </button>
                            </div>
                        </form>
                    )}

                    {!isSignup && (
                        <form onSubmit={handleSubmit} className="space-y-4" data-testid="auth-form-login">
                            <Field
                                label={t("Mobile number", "మొబైల్ నంబర్")}
                                prefix="+91"
                                inputMode="numeric"
                                value={form.phone}
                                onChange={(v) =>
                                    setForm((f) => ({ ...f, phone: v.replace(/\D/g, "").slice(0, 10) }))
                                }
                                testId="input-phone-login"
                                autoFocus
                            />
                            <Field
                                label={t("Password", "పాస్‌వర్డ్")}
                                type="password"
                                value={form.password}
                                onChange={(v) => setForm((f) => ({ ...f, password: v }))}
                                testId="input-password-login"
                            />
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full inline-flex items-center justify-between bg-[color:var(--kp-red)] text-[color:var(--kp-cream)] px-5 py-4 rounded-full text-[12px] tracking-mono-wide hover:bg-[color:var(--kp-red-deep)] disabled:opacity-60 transition-colors"
                                data-testid="auth-submit"
                            >
                                <span>
                                    {loading ? t("Signing in…", "లాగిన్…") : t("Log in", "లాగిన్")}
                                </span>
                                <ArrowRight size={14} />
                            </button>
                        </form>
                    )}

                    <div className="mt-6 pt-4 border-t border-[color:var(--kp-ink)]/10 text-sm text-[color:var(--kp-ink)]/70 text-center">
                        {isSignup ? (
                            <>
                                {t("Already have an account?", "ఇప్పటికే ఖాతా ఉందా?")}{" "}
                                <button
                                    onClick={switchMode}
                                    className="text-[color:var(--kp-red)] hover:underline"
                                    data-testid="switch-to-login"
                                >
                                    {t("Log in", "లాగిన్")}
                                </button>
                            </>
                        ) : (
                            <>
                                {t("New Customer?", "కొత్త కస్టమర్?")}{" "}
                                <button
                                    onClick={switchMode}
                                    className="text-[color:var(--kp-red)] hover:underline"
                                    data-testid="switch-to-signup"
                                >
                                    {t("Create account", "ఖాతా సృష్టించు")}
                                </button>
                            </>
                        )}
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

const Field = ({ label, value, onChange, type = "text", prefix, testId, autoFocus, inputMode }) => (
    <label className="block">
        <span className="text-[11px] tracking-mono-wide text-[color:var(--kp-ink)]/60 mb-1 block">
            {label}
        </span>
        <div className="flex items-center border border-[color:var(--kp-ink)]/25 focus-within:border-[color:var(--kp-red)] rounded-full transition-colors">
            {prefix && (
                <span className="pl-4 pr-2 text-sm text-[color:var(--kp-ink)]/60 font-serif-display">
                    {prefix}
                </span>
            )}
            <input
                type={type}
                inputMode={inputMode}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                autoFocus={autoFocus}
                data-testid={testId}
                className="flex-1 bg-transparent py-3 pr-4 pl-3 outline-none text-[color:var(--kp-ink)] placeholder:text-[color:var(--kp-ink)]/40"
            />
        </div>
    </label>
);
